/**
 * Assembling a clip out of the fMP4 fragments hls.js already produced.
 *
 * hls.js downloads each HLS segment once, transmuxes it (MPEG-TS is converted to fMP4 on the way; a
 * CMAF source is already fMP4 and passes through) and hands the result to the `SourceBuffer`. We tap
 * that same output, so cutting a clip costs no extra request, no second transmux and — crucially — no
 * re-encoding: the bytes written to disk are the camera's original picture, not a canvas re-capture.
 *
 * A finished clip is just `init segment + the fragments that overlap the window`, in timeline order.
 * `init` carries `ftyp` + `moov` (codec configuration, no samples); each fragment carries `moof` +
 * `mdat`. Concatenated in that order they form a valid, playable fMP4 file.
 *
 * ## Why the cut lands on segment boundaries
 *
 * The window is honoured at **whole-fragment granularity**. Trimming inside a fragment would mean
 * rewriting `trun` sample tables and `baseMediaDecodeTime`, and it could only ever cut at a sync sample
 * anyway. Every HLS segment already starts at a keyframe, so keeping whole fragments is the same
 * decision taken to its simplest form: the clip may open up to one segment (typically 2–6 s) before the
 * moment the user pressed record, and close up to one segment after they confirmed. For match footage
 * that lead-in is usually welcome — you see the build-up rather than landing mid-action.
 */

import { readUint32, readUint64, walkBoxes, writeUint32, writeUint64 } from '../local-data/mp4-boxes.helper';

/**
 * One transmuxed HLS segment, held in the capture buffer.
 *
 * `chunks` is a list rather than a single buffer because hls.js appends progressively: a fragment can
 * arrive as several `BUFFER_APPENDING` events while it is still being parsed. Their order of arrival
 * is their order in the file, so they are simply kept as-is and concatenated at the end.
 */
export interface CapturedFragment {
  /** Identity of the segment (`level`/`sn`), used to drop the duplicate appends a level switch causes. */
  key: string;
  /** Variant this segment came from, so a clip is never assembled out of two different resolutions. */
  level: number;
  /** Start of the fragment on the media timeline, in seconds — the same clock as `video.currentTime`. */
  start: number;
  /** End of the fragment on the media timeline, in seconds. */
  end: number;
  /** fMP4 `moof`+`mdat` bytes, in arrival order. */
  chunks: Uint8Array[];
}

export interface AssembledClip {
  blob: Blob;
  /** Length of the media actually in the file — the fragments kept, not the time the button was held. */
  durationMs: number;
  /** Media-timeline bounds of the assembled clip, for logging and diagnostics. */
  start: number;
  end: number;
}

/**
 * Whether these bytes are an init segment rather than a media fragment.
 *
 * hls.js announces the init segment through `BUFFER_CODECS` *and* then appends it like any other
 * buffer, so the capture sees it twice. Keeping that second copy would leave a stray `ftyp`/`moov`
 * in the middle of the clip, which players are entitled to choke on.
 *
 * An ISO-BMFF init segment opens with `ftyp`; a media fragment opens with `styp` or `moof`. Testing
 * the box type is more honest than trying to match the bytes against the segment we already stored,
 * and it survives hls.js handing us a different instance of the same data.
 */
export const isInitSegment = (data: Uint8Array): boolean =>
  data.length >= 8 && data[4] === 0x66 && data[5] === 0x74 && data[6] === 0x79 && data[7] === 0x70;

/** `moof > traf` is the whole path to the per-track timestamps. */
const FRAGMENT_CONTAINERS = new Set(['moof', 'traf']);

/**
 * Move the clip's media to start at zero.
 *
 * Every fragment carries, in `moof > traf > tfdt`, a `baseMediaDecodeTime` measured from the start of
 * the **stream** — not of the clip. Left alone, a clip cut forty minutes into a match opens with its
 * first sample at 2400 s: the player reports a duration of forty minutes, the scrubber starts in the
 * middle, and the bench cannot seek to the beginning because there is nothing there.
 *
 * Subtracting each track's own earliest timestamp rebases the clip to 0:00 while preserving the
 * relative offset between tracks, which is what keeps audio in sync with video in a muxed stream. The
 * subtraction happens in place — the field keeps its width — so no box size changes and no parent
 * offset has to be repaired.
 *
 * Returns false when the fragments do not parse, so the caller can keep the untouched bytes rather
 * than ship a half-rewritten file.
 */
export const rebaseFragmentTimeline = (buf: Uint8Array): boolean => {
  /** Earliest `baseMediaDecodeTime` seen per `track_ID`, and every `tfdt` that has to be shifted. */
  const earliest = new Map<number, number>();
  const timestamps: { trackId: number, offset: number, version: number }[] = [];

  // A `traf`'s track is named by its `tfhd`, which always precedes the `tfdt` inside it.
  let currentTrack = -1;

  const parsed = walkBoxes(buf, 0, buf.length, FRAGMENT_CONTAINERS, box => {
    if (box.type === 'tfhd') {
      // version(1) + flags(3), then track_ID(4)
      currentTrack = readUint32(buf, box.contentStart + 4);
      return;
    }
    if (box.type !== 'tfdt' || currentTrack < 0) return;

    const version = buf[box.contentStart];
    const offset = box.contentStart + 4; // past version + flags
    const width = version === 1 ? 8 : 4;
    if (offset + width > box.end) return;

    const value = version === 1 ? readUint64(buf, offset) : readUint32(buf, offset);
    const seen = earliest.get(currentTrack);
    if (seen === undefined || value < seen) earliest.set(currentTrack, value);
    timestamps.push({ trackId: currentTrack, offset, version });
  });

  if (!parsed) return false;

  for (const timestamp of timestamps) {
    const base = earliest.get(timestamp.trackId) ?? 0;
    const value = timestamp.version === 1 ? readUint64(buf, timestamp.offset) : readUint32(buf, timestamp.offset);
    // A discontinuity mid-clip can put a later fragment behind the one we based on; clamping keeps the
    // file valid instead of wrapping the field around.
    const rebased = Math.max(0, value - base);
    if (timestamp.version === 1) writeUint64(buf, timestamp.offset, rebased);
    else writeUint32(buf, timestamp.offset, rebased);
  }

  return true;
};

const totalLength = (parts: Uint8Array[]): number => parts.reduce((sum, part) => sum + part.length, 0);

const concat = (parts: Uint8Array[]): Uint8Array => {
  const out = new Uint8Array(totalLength(parts));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
};

/**
 * Pick the fragments overlapping `[windowStart, windowEnd]` and glue them behind `initSegment`.
 *
 * Returns null when nothing overlaps the window, which is what a recording shorter than the stream's
 * segment duration looks like — the caller should surface that as a failed take rather than write a
 * zero-length file.
 */
export const assembleClip = (
  initSegment: Uint8Array,
  fragments: CapturedFragment[],
  windowStart: number,
  windowEnd: number,
): AssembledClip | null => {
  // A fragment counts as inside the window when it overlaps it at all: the press almost always lands
  // mid-segment, and dropping that segment would cut off the very moment the user meant to capture.
  const overlapping = fragments
    .filter(fragment => fragment.end > windowStart && fragment.start < windowEnd)
    .sort((a, b) => a.start - b.start);

  if (!overlapping.length) return null;

  // `initSegment` describes the variant hls.js is appending *now*, so fragments left over from a level
  // ABR switched away from just before the press carry codec configuration this file does not contain.
  // Splicing them in yields one clip holding two resolutions under a single `moov` — which is exactly
  // the corruption the per-level key exists to prevent. Keeping only the newest level costs at most the
  // lead-in, and what remains is a file players can actually decode.
  const level = overlapping[overlapping.length - 1].level;
  const kept = overlapping.filter(fragment => fragment.level === level);
  if (kept.length !== overlapping.length) {
    console.warn('Dropped captured fragments from a superseded level', {
      level, dropped: overlapping.length - kept.length, kept: kept.length,
    });
  }

  const parts: Uint8Array[] = [initSegment];
  for (const fragment of kept) parts.push(...fragment.chunks);

  const start = kept[0].start;
  const end = kept[kept.length - 1].end;

  const media = concat(parts);
  // Without this the clip would open at its position in the match rather than at 0:00. A failure here
  // is not worth discarding the take over — the footage is still correct, only its timeline is odd.
  if (!rebaseFragmentTimeline(media)) {
    console.warn('Could not rebase the clip timeline; it will start at its position in the stream');
  }

  return {
    blob: new Blob([media], { type: 'video/mp4' }),
    durationMs: Math.max(0, (end - start) * 1000),
    start,
    end,
  };
};
